import { HANDOVER_CARS_LOCATIONS } from '@constants';
import theme from 'config/theme';

const LOGISTICS_FIELDS_MAPPER = {
  id: { label: 'Work ID', key: 'id' },
  category: {
    label: 'Category',
    key: 'category',
    options: [
      {
        text: 'Delivery',
        value: 1,
        color: theme.palette.blues.blue400,
      },
      {
        text: 'Return',
        value: 2,
        color: theme.palette.greens.green400,
      },
      {
        text: 'Service Point',
        value: 4,
        color: theme.palette.oranges.orange400,
      },
      {
        text: 'Other',
        value: 5,
        color: theme.palette.grays.gray400,
      },
      { text: 'Swap', value: 6, color: theme.palette.violets.violet400 },
      {
        text: 'Temp',
        value: 7,
        color: theme.palette.reds.codingRed400,
      },
    ],
  },
  owner: {
    label: 'Owner',
    key: 'owner',
    options: [
      { text: 'Logistics', value: 1 },
      { text: 'Repairs', value: 2 },
      { text: 'Customer Success', value: 3 },
      { text: 'Marketing', value: 4 },
      { text: 'Management', value: 5 },
      { text: 'Sales', value: 6 },
      { text: 'Procurement', value: 7 },
      { text: 'HR', value: 8 },
    ],
  },
  assignee: { label: 'Driver', key: 'assignee' },
  servicePoint: {
    label: 'Service Point',
    key: 'servicePoint',
    options: Object.keys(HANDOVER_CARS_LOCATIONS)
      .filter(
        (key) =>
          HANDOVER_CARS_LOCATIONS[key] !==
          'Athens International Airport "Eleftherios Venizelos"'
      )
      .map((key) => ({
        text: HANDOVER_CARS_LOCATIONS[key],
        value: HANDOVER_CARS_LOCATIONS[key],
      })),
  },
  scheduledDate: { label: 'Scheduled Date', key: 'scheduledDate' },
  deliveryVehiclePlate: {
    label: 'Delivery Vehicle Plate',
    key: 'deliveryVehiclePlate',
  },
  deliveryVehicle: {
    label: 'Delivery Vehicle',
    key: 'deliveryVehicle',
  },
  returnVehiclePlate: {
    label: 'Return Vehicle Plate',
    key: 'returnVehiclePlate',
  },
  returnVehicle: {
    label: 'Return Vehicle',
    key: 'returnVehicle',
  },
  deliveryVehicleId: {
    label: 'Delivery Vehicle Id',
    key: 'deliveryVehicleId',
  },
  returnVehicleId: {
    label: 'Return Vehicle Id',
    key: 'returnVehicleId',
  },
  preparation: {
    label: 'Preparation',
    key: 'preparation',
    options: [
      { text: 'HQ', value: 'hq' },
      { text: 'New way', value: 'newWay' },
      { text: 'Pazaropoulos', value: 'pazaropoulos' },
      { text: 'Skg', value: 'skg' },
      { text: 'Viden', value: 'viden' },
      { text: '3PL', value: '3pl' },
    ],
  },
  pickUpLocation: { label: 'Pick Up Location', key: 'pickupLocation' },
  dropOffLocation: { label: 'Drop Off Location', key: 'dropoffLocation' },
  currentLocation: { label: 'Current Location', key: 'currentLocation' },
  recipientName: { label: 'Recipient Name', key: 'recipientName' },
  address: { label: 'Address', key: 'address' },
  region: { label: 'Region', key: 'region' },
  postalCode: { label: 'Postal Code', key: 'postalCode' },
  phoneNumber: { label: 'Phone Number', key: 'phoneNumber' },
  tags: {
    label: 'Labels',
    key: 'tags',
    options: [
      { text: 'Car Swap', value: 'carSwap' },
      { text: 'Car Swap / Predel', value: 'carSwapPredel' },
      { text: 'Car Swap / Temp', value: 'carSwapTemp' },
      { text: 'Predelivery', value: 'predelivery' },
      { text: 'Temporary', value: 'temporary' },
      { text: 'Employee', value: 'employee' },
      { text: 'Instadelivery No fee', value: 'instadeliveryNoFee' },
      {
        text: 'Temporary & Drop off Vehicle',
        value: 'temporaryDropOffVehicle',
      },
    ],
  },
  comment: { label: 'Comment', key: 'comment' },
  startDate: { label: 'Start Date', key: 'startDate' },
  endDate: { label: 'End Date', key: 'endDate' },
  primaryDriver: { label: 'Primary Driver', key: 'primaryDriver' },
  secondaryDriver: { label: 'Secondary Driver', key: 'secondaryDriver' },
  status: {
    label: 'Status',
    key: 'status',
    options: [
      { text: 'Cancelled', value: 12 },
      { text: 'Unplanned', value: 74 },
      { text: 'In Progress', value: 2 },
      { text: 'Done', value: 75 },
    ],
  },
  createdAt: { label: 'Created At', key: 'createdAt' },
  completedAt: { label: 'Completed At', key: 'completedAt' },
};

const INITIAL_LOGISTICS_GRID_COLUMNS = [
  ...Object.values(LOGISTICS_FIELDS_MAPPER)
    .filter((field) => field?.key !== LOGISTICS_FIELDS_MAPPER.comment.key)
    .map((field) => ({
      ...field,
      value: true,
    })),
];

const LOGISTICS_PAGE_SIZE = 200;

const DEFAULT_DROPDOWN_OPTION = {
  text: '-',
  value: undefined,
};

const DEFAULT_STRING_DROPDOWN_OPTION = {
  text: '-',
  value: '',
};

const DEFAULT_NUMERIC_DROPDOWN_OPTION = {
  text: '-',
  value: 0,
};

const LOGISTICS_DATE_FORMAT = 'dd-MM-yyyy HH:mm';

const LOGISTICS_DEFAULT_ORDER = 'desc';

const LOGISTICS_DEFAULT_ORDER_BY = LOGISTICS_FIELDS_MAPPER.createdAt.key;

const LOGISTICS_EXPORT_LIMIT = 100000;

const LOGISTICS_SORTING_KEYS = {
  [LOGISTICS_FIELDS_MAPPER.createdAt.key]:
    LOGISTICS_FIELDS_MAPPER.createdAt.key,
  [LOGISTICS_FIELDS_MAPPER.id.key]: LOGISTICS_FIELDS_MAPPER.id.key,
  [LOGISTICS_FIELDS_MAPPER.scheduledDate
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.scheduledDate.key}`,
  [LOGISTICS_FIELDS_MAPPER.status.key]: LOGISTICS_FIELDS_MAPPER.status.key,
  [LOGISTICS_FIELDS_MAPPER.primaryDriver
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.primaryDriver.key}`,
  [LOGISTICS_FIELDS_MAPPER.secondaryDriver
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.secondaryDriver.key}`,
  [LOGISTICS_FIELDS_MAPPER.pickUpLocation
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.pickUpLocation.key}`,
  [LOGISTICS_FIELDS_MAPPER.dropOffLocation
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.dropOffLocation.key}`,
};

const LOGISTICS_FILTERS_KEYS = {
  [LOGISTICS_FIELDS_MAPPER.category
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.category.key}`,
  [LOGISTICS_FIELDS_MAPPER.status.key]: LOGISTICS_FIELDS_MAPPER.status.key,
  [LOGISTICS_FIELDS_MAPPER.assignee.key]: LOGISTICS_FIELDS_MAPPER.assignee.key,
  [LOGISTICS_FIELDS_MAPPER.servicePoint
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.servicePoint.key}`,
  [LOGISTICS_FIELDS_MAPPER.scheduledDate
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.scheduledDate.key}`,
  [LOGISTICS_FIELDS_MAPPER.startDate
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.startDate.key}`,
  [LOGISTICS_FIELDS_MAPPER.pickUpLocation
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.pickUpLocation.key}`,
  [LOGISTICS_FIELDS_MAPPER.dropOffLocation
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.dropOffLocation.key}`,
  [LOGISTICS_FIELDS_MAPPER.primaryDriver
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.primaryDriver.key}`,
  [LOGISTICS_FIELDS_MAPPER.secondaryDriver
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.secondaryDriver.key}`,
  [LOGISTICS_FIELDS_MAPPER.owner
    .key]: `customFields.${LOGISTICS_FIELDS_MAPPER.owner.key}`,
  [LOGISTICS_FIELDS_MAPPER.completedAt.key]:
    LOGISTICS_FIELDS_MAPPER.completedAt.key,
};

export const LOGISTICS_DOCUMENT_TYPES = [
  {
    text: 'Κλήση',
    value: 48,
  },
  {
    text: 'Αποδεικτικό Πληρωμής',
    value: 18,
  },
  {
    text: 'Ταυτότητα',
    value: 16,
  },
  {
    text: 'Υπεύθυνη Δήλωση',
    value: 21,
  },
  {
    text: 'Εκπροσώπηση Εταιρείας',
    value: 49,
  },
  {
    text: 'Καταστατικό Εταιρείας',
    value: 50,
  },
  {
    text: 'Άλλο',
    value: 27,
  },
];
export {
  LOGISTICS_DATE_FORMAT,
  LOGISTICS_DEFAULT_ORDER,
  LOGISTICS_DEFAULT_ORDER_BY,
  LOGISTICS_FIELDS_MAPPER,
  INITIAL_LOGISTICS_GRID_COLUMNS,
  LOGISTICS_PAGE_SIZE,
  DEFAULT_DROPDOWN_OPTION,
  DEFAULT_STRING_DROPDOWN_OPTION,
  DEFAULT_NUMERIC_DROPDOWN_OPTION,
  LOGISTICS_SORTING_KEYS,
  LOGISTICS_FILTERS_KEYS,
  LOGISTICS_EXPORT_LIMIT,
};
